import { IFindPageQueryString, IMetaData, TypeFacetGroup } from "models/post";
import {
  IListingAssets,
  IListingState,
} from "contexts/ListingContextHelpers/interfaces";
import { isCategoryRealEstate } from "helpers/osHelpers";
import {
  IAllCPsMap,
  ICpOption,
  IDynamicAttributeEnhanced,
  IDynamicAttributeSortField,
} from "models/Assets/IDynamicAttributes";
import { ICategoryLiteAsset } from "models/Assets/ICategoryAsset";
import {
  _qsUnlinkableCPS,
  linkToCustomListing,
  linkToDLP,
  linkToFind,
  linkToListing,
} from "helpers/links-builders/listing";
import { flattenObject, qsStringify } from "helpers/globalHelpers";
import { TypeRouterApiBasePath } from "models/ILiteRouterApiResponse";
import { isBrowser } from "utils/nookies/utils";
import { EnumOrderMode } from "../../enums/EnumOrderMode";
import {
  getClickStreamSearchSource,
  setClickStreamSearchSource,
} from "../../libs/clickstream/helpers/clickStreamHelpers";
import { RECOMMENDED_SERP, SEARCH_SOURCE, SIMILAR_SERP } from "utils/constants";

export const processFacets = (
  _meta: IMetaData,
  assets: IListingAssets,
  category: ICategoryLiteAsset,
  subCategory: ICategoryLiteAsset,
  basePath: TypeRouterApiBasePath
): TypeFacetGroup => {
  if (basePath) {
    return { customSubCategories: _meta.pwaFacet?.subcategories || [] };
  }

  if (!category) {
    return {
      categories: _meta.pwaFacet?.categories || [],
    };
  }
  if (!subCategory) {
    if (_meta.pwaFacet?.subcategories) {
      return {
        subcategories: _meta.pwaFacet.subcategories || [],
      };
    }
  }

  if (isCategoryRealEstate(category)) {
    if (_meta?.pwaFacet?.cities) {
      return {
        cities: _meta.pwaFacet.cities || [],
      };
    }
    if (_meta?.pwaFacet?.neighborhoods) {
      return {
        neighborhoods: _meta.pwaFacet.neighborhoods,
      };
    }
  }
  if (!_meta) return null;
  const [fieldName] = Object.keys(_meta.pwaFacet?.dynamicParams || {});
  if (!fieldName) return null;
  const { is_indexable, is_linkable } = _meta.pwaFacet.dynamicParams[fieldName];
  if (!is_indexable || !is_linkable) return null;
  return _meta.pwaFacet.dynamicParams;
};

export const processSelectedSortingFields = (
  sortingFields: IDynamicAttributeSortField[],
  findPageQs: IFindPageQueryString
): IDynamicAttributeSortField => {
  let { sort = null, orderMode = null } = findPageQs.PostSearch || {};
  if (!orderMode) return sortingFields.find((sortingField) => sortingField.orderMode == Number(EnumOrderMode.NEWEST));
  let output: IDynamicAttributeSortField = null;
  orderMode = Number(orderMode);
  if (orderMode === EnumOrderMode.CUSTOM_ORDER_MODE) {
    [output] = sortingFields.filter(
      (sortingField) => sortingField.solrField === sort
    );
  } else {
    [output] = sortingFields.filter(
      (sortingField) => sortingField.orderMode === orderMode
    );
  }
  return output;
};

export const __filterSelectedOptions = (
  cpsAllMap: IAllCPsMap,
  selectedCpsOptions: ICpOption[],
  targetOptionOrOptions: ICpOption | ICpOption[],
  assets: IListingAssets
): [ICpOption[], IAllCPsMap] => {
  let selectedOptions: ICpOption[] = [...selectedCpsOptions];
  const targetOptions: ICpOption[] = Array.isArray(targetOptionOrOptions)
    ? targetOptionOrOptions
    : [targetOptionOrOptions];
  const [attr] = (assets.dynamicAttributeEnhancedAssets || []).filter(
    (attr) => targetOptions[0].fieldName === attr.fieldName
  );
  const [theParentAttr = null] = (
    assets.dynamicAttributeEnhancedAssets || []
  ).filter((attr) => attr.field.hasChild);
  const { options: allPossibleOptions } = attr || {};

  //targetOptions
  if (cpsAllMap[attr?.fieldName]) {
    selectedOptions = [...selectedOptions, ...allPossibleOptions].filter(
      (o1) => targetOptions.findIndex((o2) => o2.id === o1.id) === -1
    );
  } else {
    const isRemove =
      selectedOptions.findIndex((o) => o.id === targetOptions[0].id) > -1;
    selectedOptions = selectedOptions.filter(
      (o1) => targetOptions.findIndex((o2) => o2.id === o1.id) === -1
    );
    if (!isRemove) {
      selectedOptions = [...selectedOptions, ...targetOptions];
    } else {
      // we want to clean all linkabke cps coming after the selected option.
      //e.g clearing Car_year and Regional Specs when user click on "Model i.e avante"
      const linkableFieldsToClean: string[] = [];
      let canAppend;
      (assets.dynamicAttributeEnhancedAssets || []).forEach((attr2) => {
        if (attr2.fieldName === attr?.fieldName) {
          canAppend = true;
          return;
        }
        if (canAppend && attr2.field?.is_linkable) {
          linkableFieldsToClean.push(attr2?.fieldName);
        }
      });
      selectedOptions = selectedOptions.filter(
        (o) => linkableFieldsToClean.indexOf(o.fieldName) === -1
      );
    }
  }

  const parentSelectedOptions = (selectedOptions || []).filter(
    (c) => theParentAttr && c.fieldName === theParentAttr.fieldName
  );

  const parentId =
    parentSelectedOptions.length === 1
      ? Number(parentSelectedOptions[0].id)
      : null;

  let newSelectedOptions = (selectedOptions || []).filter(
    (o) => !o.parent_id || o.parent_id === parentId
  );

  (assets.dynamicAttributeEnhancedAssets || []).forEach((attr) => {
    const { fieldName: fName, options } = attr;
    cpsAllMap[fName] =
      options.length !== 0 &&
      options.length > 2 &&
      options.length ===
        newSelectedOptions.filter((o) => o.fieldName === fName).length;
    if (cpsAllMap[fName]) {
      newSelectedOptions = newSelectedOptions.filter(
        (o) => o.fieldName !== fName
      );
    }
  });
  Object.keys(cpsAllMap).forEach((k) => {
    if (!cpsAllMap[k]) {
      delete cpsAllMap[k];
    }
  });

  return [newSelectedOptions, cpsAllMap];
};
export const __filterSelectedGroup = (
  cpsAllMap: IAllCPsMap,
  selectedCpsOptions: ICpOption[],
  targetOptionOrOptions: ICpOption | ICpOption[],
  assets: IListingAssets
): [ICpOption[], IAllCPsMap] => {
  let newSelectedOptions = [];

  function isAllSelected(firstArr, secondArr) {
    return secondArr.every((ele) =>
      firstArr.some((item) => item.id === ele.id)
    );
  }

  const selectedCpsOptionsWithoutGroupsOps = selectedCpsOptions.filter(
    (op) => op?.group_id != targetOptionOrOptions?.[0].group_id
  );

  if (isAllSelected(selectedCpsOptions, targetOptionOrOptions)) {
    newSelectedOptions = selectedCpsOptionsWithoutGroupsOps;
  } else {
    newSelectedOptions = [
      ...selectedCpsOptionsWithoutGroupsOps,
      ...(targetOptionOrOptions as ICpOption[]),
    ];
  }

  return [newSelectedOptions, cpsAllMap];
};

export const getCpsByLinkableFields = (
  dynamicAttributeEnhancedAssets: IDynamicAttributeEnhanced[],
  selectedCpsOptions: ICpOption[]
) => {
  let linkable = [];
  let linkableChildToParentMap = {};
  let firstFieldLinkable = [];
  let unlinkable = [];
  (dynamicAttributeEnhancedAssets || []).forEach((attr, index) => {
    const ops = selectedCpsOptions.filter(
      (o) => o.fieldName === attr.fieldName
    );
    //if (ops.length == 0) return;
    if (attr.field.is_linkable) {
      const prevOps = selectedCpsOptions.filter(
        (o) => o.fieldName === attr.prevLinkableFieldName
      );
      if (prevOps.length === 1) {
        linkableChildToParentMap[attr.fieldName] = [...firstFieldLinkable];
      }
      if (ops.length !== 0) {
        linkable = [...linkable, ...ops];
        firstFieldLinkable = [...firstFieldLinkable, ops[0]];
      }
    } else {
      unlinkable = [...unlinkable, ...ops];
    }
  });
  return { linkable, firstFieldLinkable, linkableChildToParentMap, unlinkable };
};

export const getUrlPage = (state: IListingState): string => {
  const SearchSource = getClickStreamSearchSource();
  if (isBrowser() && window.location.href.includes("utm_"))
    return window.location.href;
  if (isBrowser() && window.location.href.includes("source=logout"))
    return window.location.href;
  if (isBrowser() && window.location.href.includes("searchSource=deeplink"))
    if (
      SearchSource != SEARCH_SOURCE.RECENT_SEARCH &&
      SearchSource != SEARCH_SOURCE.SAVE_SEARCH
    )
      setClickStreamSearchSource("deeplink");
  const hash =
    isBrowser() && window.location.hash.includes("size")
      ? window.location.hash
      : "";
  const {
    queryString,
    category,
    subCategory,
    city,
    neighborhoods,
    selectedCpsOptions,
    currency,
    dynamicRangeAttributes,
    assets: { dynamicAttributeEnhancedAssets },
    basePath,
  } = state;
  if (queryString.dlpSlug) {
    return linkToDLP(queryString.dlpSlug, queryString.page) + hash;
  }
  const { linkable, firstFieldLinkable, unlinkable } = getCpsByLinkableFields(
    dynamicAttributeEnhancedAssets,
    selectedCpsOptions
  );

  const isRealEstate = isCategoryRealEstate(category);

  const _getQs = (): string => {
    let dynamicAttributes = null;
    Object.keys(dynamicRangeAttributes).forEach((fieldName) => {
      if (
        !dynamicRangeAttributes[fieldName].from &&
        !dynamicRangeAttributes[fieldName].to
      )
        return;
      dynamicAttributes = dynamicAttributes || {};
      dynamicAttributes[fieldName] = {
        from: dynamicRangeAttributes[fieldName].from || null,
        to: dynamicRangeAttributes[fieldName].to || null,
        unit: dynamicRangeAttributes?.[fieldName]?.unit?.id || null,
      };
    });
    const term = queryString?.term;
    delete queryString?.PostSearch?.term;
    const qs: IFindPageQueryString = {
      "landing-popup": queryString?.["landing-popup"]
        ? queryString["landing-popup"]
        : null,
      similarSerp: queryString?.[SIMILAR_SERP]
        ? queryString[SIMILAR_SERP]
        : null,
      recommendedSerp: queryString?.[RECOMMENDED_SERP]
        ? queryString[RECOMMENDED_SERP]
        : null,
      includeSelf: queryString?.includeSelf ? queryString.includeSelf : null,
      page: state.pageNumber && state.pageNumber > 1 ? state.pageNumber : null,
      filterFlags: queryString.filterFlags,
      term,
      PostSearch: {
        ...queryString.PostSearch,
        page: null,
        orderMode:
          queryString.PostSearch.orderMode == Number(EnumOrderMode.NEWEST)
            ? null
            : queryString.PostSearch.orderMode,
        price_currency: currency?.isDefault
          ? null
          : queryString.PostSearch.price_currency,
        perPage: null,
        categoryId: null,
        subCategoryId: null,
        cityId: null,
        neighborhood_id: null,
        categoriesId: null,
        dynamicAttributes,
      },
      ...(term && { search: true }),
    };
    return qsStringify(qs);
  };

  const _getCustomQs = (): string => {
    let rangeAttributes = {};
    if (queryString?.PostSearch?.dynamicAttributes) {
      for (const key in queryString?.PostSearch.dynamicAttributes) {
        if (!Array.isArray(queryString?.PostSearch.dynamicAttributes[key])) {
          rangeAttributes = {
            ...rangeAttributes,
            ...(flattenObject({
              [key]: queryString?.PostSearch.dynamicAttributes[key],
            }) || {}),
          };
        }
      }
    }

    const neighborhoodsIds = neighborhoods?.[0]
      ? isRealEstate
        ? neighborhoods
            .slice(1)
            .map((n) => n.id)
            .join(",")
        : neighborhoods.map((n) => n.id).join(",")
      : null;

    let { priceFrom, priceTo, price_currency, term } = queryString?.PostSearch;
    let customQs = {
      "landing-popup": queryString?.["landing-popup"]
        ? queryString["landing-popup"]
        : null,
      similarSerp: queryString?.[SIMILAR_SERP]
        ? queryString[SIMILAR_SERP]
        : null,
      recommendedSerp: queryString?.[RECOMMENDED_SERP]
        ? queryString[RECOMMENDED_SERP]
        : null,
      page: state.pageNumber && state.pageNumber > 1 ? state.pageNumber : null,
      priceFrom,
      priceTo,
      price_currency: priceFrom || priceTo ? price_currency : null,
      term,
      ...rangeAttributes,
      ...{ filterFlags: queryString.filterFlags },
    }
    neighborhoodsIds ? customQs["neighborhoods"] = neighborhoodsIds : null

    return qsStringify(customQs);
  };

  const _linkListing = (customlinkable = null, withCustomQS = false) => {
    let customLinkable = customlinkable || linkable;
    const url = linkToListing([
      city,
      isRealEstate && neighborhoods?.[0],
      category,
      subCategory ? subCategory : { url_name: "all" },
      ...customLinkable,
    ]);

    return `${url}${withCustomQS ? _getCustomQs() : _getQs()}`;
  };

  if (category && !subCategory) {
    return _linkListing() + hash;
  }
  const _linkCustomListing = () => {
    const url = linkToCustomListing(basePath, [city, neighborhoods?.[0]]);
    return url + _getQs();
  };

  const _linkToFind = (): string => {
    const findUrl = linkToFind({
      basePath,
      currency: currency?.isDefault ? null : currency,
      allCPsMap: queryString.allCPsMap,
      filterFlags: queryString.filterFlags,
      cities: [city],
      neighborhoods,
      category: category || { id: queryString.PostSearch.categoryId },
      subCategory: subCategory || { id: queryString.PostSearch.subCategoryId },
      page: queryString.page,
      ...queryString.PostSearch,
      orderMode:
        queryString.PostSearch.orderMode == Number(EnumOrderMode.NEWEST)
          ? null
          : queryString.PostSearch.orderMode,
      categoriesId: !queryString.PostSearch.categoriesId
        ? null
        : queryString.PostSearch.categoriesId,
      dynamicAttributesIds: queryString.PostSearch.dynamicAttributes,
      similarSerp: queryString.similarSerp ? queryString.similarSerp : null,
      recommendedSerp: queryString.recommendedSerp ? queryString.recommendedSerp : null,
      includeSelf: queryString.includeSelf ? queryString.includeSelf : null,
      landingPopup: queryString["landing-popup"] ? queryString["landing-popup"] : null
    });
    return findUrl;
  };

  if (basePath) {
    if (neighborhoods.length > 1) return _linkToFind() + hash;
    return _linkCustomListing() + hash;
  }


  if (
    (!window.location.search ||
      window.location.search.match(/\?page=\d+$/) ||
      window.location.search.match(/\?landing-popup=\d+$/) ||
      window.location.search.match(/\?similarSerp=\d+$/) ||
      window.location.search.match(/\?recommendedSerp=\d+$/)
    ) &&
    (queryString.filterFlags || []).length === 0 &&
    Object.keys(queryString.allCPsMap || {}).length === 0 &&
    Object.keys(dynamicRangeAttributes || {}).length === 0 &&
    (category || subCategory) &&
    !isRealEstate &&
    neighborhoods.length === 0 &&
    unlinkable.length === 0 &&
    linkable.length === firstFieldLinkable?.length
  ) {
    const LinkableFieldsList = dynamicAttributeEnhancedAssets?.filter(
      (op) => op?.field?.is_linkable
    );

    if (
      selectedCpsOptions?.length &&
      selectedCpsOptions?.length <= LinkableFieldsList?.length
    ) {
      const result = LinkableFieldsList?.map((field) => {
        const isSelected = selectedCpsOptions?.some(
          (option) => option?.fieldName === field?.fieldName
        );
        return isSelected ? 1 : 0;
      });

      for (let i = 0; i < result?.length; i++) {
        if (result[i] === 0) {
          const restOfArray = result?.slice(i + 1);
          if (!restOfArray?.includes(1)) {
            return _linkListing() + hash;
          }
          return _linkToFind() + hash;
        }
      }
      return _linkListing() + hash;
    }

    return _linkListing() + hash;
  }

  if ((isBrowser() && window.location.href.includes("find")) || !category)
    return _linkToFind() + hash;

  const { linkableDuplicates, uniqueLinkable, fieldNames } = linkable.reduce(
    (acc, obj) => {
      if (acc.fieldNames.has(obj.fieldName)) {
        acc.linkableDuplicates.push(obj);
      } else {
        acc.fieldNames.add(obj.fieldName);
        acc.uniqueLinkable.push(obj);
      }
      return acc;
    },
    { linkableDuplicates: [], uniqueLinkable: [], fieldNames: new Set() }
  );

  let unlinkableCPS = _qsUnlinkableCPS({
    dynamicAttributesObjects: [...unlinkable, ...linkableDuplicates],
  });
  let seoFriendlyLink = _linkListing(uniqueLinkable, true);

  // to handle /en/cars/cars-for-sale/opel/opel-gt/2023?Car_Year_from=2013&Car_Year_to=2023
  if (seoFriendlyLink.includes("Car_Year")) {
    seoFriendlyLink = _linkListing(
      uniqueLinkable.filter((op) => op.fieldName !== "Car_Year"),
      true
    );
  }

  unlinkableCPS = unlinkableCPS
    ? `${seoFriendlyLink.includes("?") ? "&" : "?"}${unlinkableCPS}`
    : "";
  let friendlyLink = `${seoFriendlyLink}${unlinkableCPS}${hash}`
  friendlyLink.includes("?") ? friendlyLink += queryString.PostSearch?.orderMode ? `&PostSearch[orderMode]=${queryString.PostSearch.orderMode}` : "" : friendlyLink += queryString.PostSearch?.orderMode ? `?PostSearch[orderMode]=${queryString.PostSearch.orderMode}` : "";
  friendlyLink += queryString.PostSearch?.sort ? `&PostSearch[sort]=${queryString.PostSearch.sort}` : "";
  return (queryString?.similarSerp || queryString?.recommendedSerp) ? friendlyLink : friendlyLink?.replace("?", "?search=true&");
};
